/**
 * Google Sheets Category Repository
 *
 * Google Sheets API를 사용하여 실제 스프레드시트에서 카테고리 데이터를 관리합니다.
 */

import type { Category } from '../types/Category'
import type { CategoryRepository } from './CategoryRepository'
import { GoogleSheetsClient } from '@/core/api/GoogleSheetsClient'
import { SHEET_NAMES } from '@/core/config/constants'
import { getWorkspaceClient } from '@/core/workspace/WorkspaceContext'

/**
 * Spreadsheet 행을 Category 객체로 변환
 *
 * 첫 행은 헤더이므로 2행부터 데이터입니다.
 * 컬럼 순서: id, name, icon, type, level, parentId, sortOrder, isDefault, isActive
 */
function rowToCategory(row: string[]): Category {
  return {
    id: row[0] || '',
    name: row[1] || '',
    icon: row[2] || '❓',
    type: (row[3] || 'expense') as 'income' | 'expense',
    level: (row[4] || 'major') as 'major' | 'minor',
    parentId: row[5] || null,
    sortOrder: parseInt(row[6] || '0', 10),
    isDefault: row[7] === 'true' || row[7] === 'TRUE',
    isActive: row[8] === 'true' || row[8] === 'TRUE',
  }
}

/**
 * Category 객체를 Spreadsheet 행으로 변환
 *
 * 컬럼 순서: id, name, icon, type, level, parentId, sortOrder, isDefault, isActive
 */
function categoryToRow(category: Category): string[] {
  return [
    category.id,
    category.name,
    category.icon,
    category.type,
    category.level,
    category.parentId || '',
    category.sortOrder.toString(),
    category.isDefault.toString(),
    category.isActive.toString(),
  ]
}

/**
 * Flat 카테고리 배열을 계층 구조로 변환
 *
 * @param flatCategories - Flat 카테고리 배열
 * @returns 대분류 카테고리 배열 (children 속성에 소분류 포함)
 */
function buildCategoryHierarchy(flatCategories: Category[]): Category[] {
  const majors = flatCategories.filter((c) => c.level === 'major')
  const minors = flatCategories.filter((c) => c.level === 'minor')

  return majors.map((major) => ({
    ...major,
    children: minors
      .filter((minor) => minor.parentId === major.id)
      .sort((a, b) => a.sortOrder - b.sortOrder),
  }))
}

/**
 * Google Sheets Category Repository 구현
 */
export class GoogleSheetsCategoryRepository implements CategoryRepository {
  constructor(_client?: GoogleSheetsClient) {
    // client 파라미터는 테스트 호환성을 위해 유지하지만 사용하지 않음
  }

  /**
   * 현재 워크스페이스의 GoogleSheetsClient 가져오기
   */
  private getClient(): GoogleSheetsClient {
    return getWorkspaceClient()
  }

  /**
   * 모든 카테고리 조회
   */
  async findAll(): Promise<Category[]> {
    // 시트에서 모든 데이터 읽기
    const rows = await this.getClient().readSheet(SHEET_NAMES.CATEGORIES)

    // 첫 행은 헤더이므로 제외
    const dataRows = rows.slice(1)

    // 각 행을 Category 객체로 변환
    const flatCategories = dataRows
      .filter((row) => row.length > 0) // 빈 행 제외
      .filter((row) => row[8] !== 'false' && row[8] !== 'FALSE') // isActive=false 제외
      .map(rowToCategory)
      .sort((a, b) => a.sortOrder - b.sortOrder)

    // 계층 구조로 변환
    return buildCategoryHierarchy(flatCategories)
  }

  /**
   * 타입별 카테고리 조회
   */
  async findByType(type: 'income' | 'expense'): Promise<Category[]> {
    const allCategories = await this.findAll()
    return allCategories.filter((cat) => cat.type === type)
  }

  /**
   * ID로 카테고리 조회
   */
  async findById(id: string): Promise<Category | null> {
    const allCategories = await this.findAll()

    // 대분류에서 찾기
    for (const major of allCategories) {
      if (major.id === id) {
        return major
      }

      // 소분류에서 찾기
      if (major.children) {
        const minor = major.children.find((c) => c.id === id)
        if (minor) {
          return minor
        }
      }
    }

    return null
  }

  /**
   * 새 카테고리 생성
   *
   * 새로운 카테고리를 Google Sheets에 추가합니다.
   * ID는 자동으로 생성됩니다.
   */
  async create(input: Omit<Category, 'id'>): Promise<Category> {
    // 1. 전체 데이터 읽기 (ID 생성을 위해)
    const rows = await this.getClient().readSheet(SHEET_NAMES.CATEGORIES)
    const dataRows = rows.slice(1)

    // 2. 새 ID 생성
    const prefix = input.type === 'expense' ? 'exp_' : 'inc_'
    const level = input.level === 'major' ? 'major' : 'minor'
    const maxId = dataRows
      .filter(row => row[0]?.startsWith(prefix))
      .map(row => {
        const id = row[0] || ''
        const match = id.match(/_(\d+)$/)
        return match ? parseInt(match[1]!, 10) : 0
      })
      .reduce((max, current) => Math.max(max, current), 0)

    const newId = `${prefix}${level}_${String(maxId + 1).padStart(3, '0')}`

    // 3. 새 카테고리 객체 생성
    const newCategory: Category = {
      id: newId,
      ...input,
    }

    // 4. Google Sheets에 추가
    await this.getClient().appendSheet(SHEET_NAMES.CATEGORIES, [categoryToRow(newCategory)])

    return newCategory
  }

  /**
   * 카테고리 수정
   *
   * 특정 카테고리의 정보를 업데이트합니다.
   */
  async update(id: string, input: Partial<Category>): Promise<Category> {
    // 1. 전체 데이터 읽기
    const rows = await this.getClient().readSheet(SHEET_NAMES.CATEGORIES)
    const dataRows = rows.slice(1)

    // 2. 해당 행 찾기
    const rowIndex = dataRows.findIndex((row) => row[0] === id)
    if (rowIndex === -1) {
      throw new Error(`카테고리를 찾을 수 없습니다: ${id}`)
    }

    // 3. 기존 데이터와 병합
    const existingRow = dataRows[rowIndex]!
    const existingCategory = rowToCategory(existingRow)
    const updated: Category = {
      ...existingCategory,
      ...input,
      id, // ID는 변경 불가
    }

    // 4. 시트 업데이트 (헤더 +1, 0-based index +1 = +2)
    const sheetRowIndex = rowIndex + 2
    const range = `A${sheetRowIndex}:I${sheetRowIndex}`
    await this.getClient().updateSheet(SHEET_NAMES.CATEGORIES, range, [categoryToRow(updated)])

    return updated
  }

  /**
   * 카테고리 삭제 (Soft Delete)
   *
   * 실제로 데이터를 삭제하는 대신 isActive를 false로 설정합니다.
   * 이는 데이터 무결성을 유지하고, 필요시 복구할 수 있도록 합니다.
   */
  async delete(id: string): Promise<void> {
    // Soft delete: isActive를 false로 설정
    await this.update(id, { isActive: false })
  }
}
